import {
  Component,
  inject,
  signal,
  OnInit,
  Inject,
  PLATFORM_ID,
  OnDestroy
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PushNotificationService } from '../../../core/services/push-notification.service';

@Component({
  selector: 'app-notification-prompt',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './notification-prompt.component.html',
  styleUrls: ['./notification-prompt.component.css'],
})
export class NotificationPromptComponent implements OnInit, OnDestroy {
  private pushService = inject(PushNotificationService);
  private router = inject(Router);

  readonly isVisible = signal(false);
  private userId: number | null = null;
  private routerSub: Subscription | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.checkAndShowPrompt();

    // Listen to route changes to handle login/logout without page refresh
    this.routerSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.checkAndShowPrompt();
      });
  }

  ngOnDestroy(): void {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
  }

  private checkAndShowPrompt(): void {
    if (this.isVisible()) return;

    // Check if user is actually logged in
    const isLoggedLocal = localStorage.getItem('isLoggedIn') === 'true';
    const isLoggedSession = sessionStorage.getItem('isLoggedIn') === 'true';
    if (!isLoggedLocal && !isLoggedSession) {
      this.isVisible.set(false);
      this.userId = null;
      return;
    }

    // Extract userId from localStorage
    try {
      const userData =
        localStorage.getItem('user') || localStorage.getItem('userData');
      if (userData) {
        const user = JSON.parse(userData);
        this.userId =
          user.id || user.user_id || user.teacher_id || user.userId;
      } else {
        this.userId = null;
      }
    } catch {
      this.userId = null;
    }

    // Only show if user is logged in and prompt conditions are met
    if (this.userId && this.pushService.shouldShowPrompt()) {
      // Delay showing the prompt so it doesn't compete with page load
      setTimeout(() => {
        if (this.pushService.shouldShowPrompt()) {
          this.isVisible.set(true);
        }
      }, 5000);
    } else {
      this.isVisible.set(false);
    }
  }

  onEnable(): void {
    if (this.userId) {
      this.pushService.subscribeToNotifications(this.userId);
    }
    this.isVisible.set(false);
  }

  onDismiss(): void {
    this.pushService.dismissPrompt();
    this.isVisible.set(false);
  }
}
